/**
 * Style Genome — the registry of complete design languages (data only).
 *
 * Each genome is an entire visual language expressed as references into the
 * shared vocabulary — never a raw literal decision, never a pixel, never an
 * editor concept. Expansion rules are semantic seeds the Thinking Engine maps
 * to V2 tokens later; the compiler and editor never see a genome.
 *
 * Genomes are deep-frozen so the registry is immutable and deterministic.
 */

/** Recursively freeze a data object (immutability for the registry). */
const deepFreeze = (obj) => {
  Object.values(obj).forEach((v) => {
    if (v && typeof v === 'object' && !Object.isFrozen(v)) deepFreeze(v);
  });
  return Object.freeze(obj);
};

const RAW = [
  {
    id: 'luxury', name: 'Luxury', family: 'luxe',
    visualPhilosophy: 'opulence', composition: 'centered-axis', layoutPhilosophy: 'image-first',
    grid: 'modular', rhythm: 'calm', spacing: 'generous', typography: 'editorial-serif',
    color: 'muted-neutral-accent', shape: 'soft-rect', decoration: 'sparse', photography: 'editorial',
    illustration: 'none', icon: 'thin-line', effects: ['subtle-shadow', 'grain'], cta: 'outline',
    hero: 'image-dominant', card: 'borderless', border: 'hairline', background: 'solid',
    premiumCues: ['restraint', 'refined-type', 'generous-whitespace', 'considered-color', 'crafted-detail'],
    avoid: ['clutter', 'rainbow-palette', 'clip-art'],
    componentFamilies: ['hero', 'gallery', 'cta', 'footer'],
    libraryPatterns: ['minimal-stack', 'image-band', 'magazine-spread'],
    diversityTags: ['luxe', 'minimal', 'editorial', 'mono'],
    expansion: { palette: 'muted-neutral-accent', typeScale: 'dramatic', radius: 'subtle', elevation: 'soft', density: 'generous', effects: ['subtle-shadow'], componentBias: { hero: 'image-dominant', cta: 'outline' } },
  },
  {
    id: 'swiss', name: 'Swiss', family: 'editorial',
    visualPhilosophy: 'precision', composition: 'modular-grid', layoutPhilosophy: 'grid-led',
    grid: 'strict-columns', rhythm: 'steady', spacing: 'comfortable', typography: 'grotesque-solo',
    color: 'high-contrast', shape: 'sharp-rect', decoration: 'none', photography: 'documentary',
    illustration: 'none', icon: 'solid', effects: ['flat'], cta: 'prominent-solid',
    hero: 'type-dominant', card: 'flat-outline', border: 'hairline', background: 'solid',
    premiumCues: ['precision', 'refined-type', 'considered-color', 'confident-scale'],
    avoid: ['clutter', 'default-fonts', 'busy-background'],
    componentFamilies: ['hero', 'features', 'stats', 'footer'],
    libraryPatterns: ['editorial-columns', 'feature-row', 'stat-row'],
    diversityTags: ['type-led', 'geometric', 'flat', 'sharp'],
    expansion: { palette: 'high-contrast', typeScale: 'dramatic', radius: 'none', elevation: 'flat', density: 'comfortable', effects: ['flat'], componentBias: { hero: 'type-dominant' } },
  },
  {
    id: 'editorial', name: 'Editorial', family: 'editorial',
    visualPhilosophy: 'craft', composition: 'editorial-columns', layoutPhilosophy: 'story-led',
    grid: 'baseline-locked', rhythm: 'flowing', spacing: 'airy', typography: 'serif-display-sans-body',
    color: 'monochrome', shape: 'sharp-rect', decoration: 'line-motifs', photography: 'editorial',
    illustration: 'none', icon: 'thin-line', effects: ['flat', 'subtle-shadow'], cta: 'text-link',
    hero: 'split', card: 'borderless', border: 'hairline', background: 'solid',
    premiumCues: ['refined-type', 'generous-whitespace', 'crafted-detail', 'considered-color'],
    avoid: ['clutter', 'default-fonts', 'low-contrast'],
    componentFamilies: ['hero', 'features', 'gallery', 'footer'],
    libraryPatterns: ['magazine-spread', 'editorial-columns', 'image-band'],
    diversityTags: ['editorial', 'type-led', 'mono', 'minimal'],
    expansion: { palette: 'monochrome', typeScale: 'dramatic', radius: 'none', elevation: 'flat', density: 'airy', effects: ['flat'], componentBias: { hero: 'split' } },
  },
  {
    id: 'minimal', name: 'Minimal', family: 'minimal',
    visualPhilosophy: 'restraint', composition: 'symmetric', layoutPhilosophy: 'whitespace-led',
    grid: 'strict-columns', rhythm: 'calm', spacing: 'airy', typography: 'geometric-sans',
    color: 'monochrome', shape: 'soft-rect', decoration: 'none', photography: 'studio-clean',
    illustration: 'none', icon: 'thin-line', effects: ['flat'], cta: 'outline',
    hero: 'minimal', card: 'borderless', border: 'none', background: 'solid',
    premiumCues: ['restraint', 'generous-whitespace', 'refined-type', 'precision'],
    avoid: ['clutter', 'busy-background', 'rainbow-palette'],
    componentFamilies: ['hero', 'features', 'cta', 'footer'],
    libraryPatterns: ['minimal-stack', 'centered-poster', 'feature-row'],
    diversityTags: ['minimal', 'flat', 'mono', 'soft'],
    expansion: { palette: 'monochrome', typeScale: 'balanced', radius: 'medium', elevation: 'flat', density: 'airy', effects: ['flat'], componentBias: { hero: 'minimal' } },
  },
  {
    id: 'bold', name: 'Bold', family: 'expressive',
    visualPhilosophy: 'maximalism', composition: 'diagonal', layoutPhilosophy: 'type-first',
    grid: 'broken-grid', rhythm: 'punchy', spacing: 'compact', typography: 'condensed-bold',
    color: 'high-contrast', shape: 'angular', decoration: 'geometric-accents', photography: 'moody',
    illustration: 'abstract-shapes', icon: 'sharp', effects: ['hard-shadow'], cta: 'oversized',
    hero: 'type-dominant', card: 'filled', border: 'bold', background: 'solid',
    premiumCues: ['confident-scale', 'considered-color'],
    avoid: ['low-contrast', 'tiny-cta', 'clutter'],
    componentFamilies: ['hero', 'cta', 'features', 'stats'],
    libraryPatterns: ['centered-poster', 'feature-row', 'stat-row'],
    diversityTags: ['maximal', 'type-led', 'sharp', 'vibrant'],
    expansion: { palette: 'high-contrast', typeScale: 'dramatic', radius: 'none', elevation: 'pronounced', density: 'compact', effects: ['hard-shadow'], componentBias: { hero: 'type-dominant', cta: 'oversized' } },
  },
  {
    id: 'corporate', name: 'Corporate', family: 'corporate',
    visualPhilosophy: 'clarity', composition: 'modular-grid', layoutPhilosophy: 'content-first',
    grid: 'strict-columns', rhythm: 'steady', spacing: 'comfortable', typography: 'humanist-sans',
    color: 'brand-forward', shape: 'soft-rect', decoration: 'sparse', photography: 'studio-clean',
    illustration: 'flat-vector', icon: 'solid', effects: ['subtle-shadow'], cta: 'prominent-solid',
    hero: 'split', card: 'elevated', border: 'hairline', background: 'subtle-gradient',
    premiumCues: ['precision', 'considered-color', 'refined-type', 'confident-scale'],
    avoid: ['clutter', 'clip-art', 'busy-background'],
    componentFamilies: ['hero', 'features', 'stats', 'pricing', 'contact', 'footer'],
    libraryPatterns: ['feature-row', 'stat-row', 'card-stack'],
    diversityTags: ['technical', 'flat', 'mono'],
    expansion: { palette: 'brand-forward', typeScale: 'balanced', radius: 'medium', elevation: 'soft', density: 'comfortable', effects: ['subtle-shadow'], componentBias: { hero: 'split' } },
  },
  {
    id: 'playful', name: 'Playful', family: 'playful',
    visualPhilosophy: 'warmth', composition: 'freeform', layoutPhilosophy: 'image-first',
    grid: 'loose-columns', rhythm: 'dynamic', spacing: 'comfortable', typography: 'expressive-display',
    color: 'pastel', shape: 'pill', decoration: 'organic-accents', photography: 'bright-airy',
    illustration: 'hand-drawn', icon: 'rounded', effects: ['soft-elevation'], cta: 'pill-accent',
    hero: 'image-dominant', card: 'filled', border: 'none', background: 'subtle-gradient',
    premiumCues: ['tactile', 'considered-color', 'crafted-detail'],
    avoid: ['low-contrast', 'clutter'],
    componentFamilies: ['hero', 'features', 'gallery', 'cta'],
    libraryPatterns: ['card-stack', 'feature-row', 'collage-wall'],
    diversityTags: ['playful', 'organic', 'soft', 'vibrant'],
    expansion: { palette: 'pastel', typeScale: 'balanced', radius: 'round', elevation: 'soft', density: 'comfortable', effects: ['soft-elevation'], componentBias: { cta: 'pill-accent' } },
  },
  {
    id: 'glassmorphism', name: 'Glassmorphism', family: 'technical',
    visualPhilosophy: 'futurism', composition: 'layered-overlap', layoutPhilosophy: 'content-first',
    grid: 'modular', rhythm: 'flowing', spacing: 'comfortable', typography: 'geometric-sans',
    color: 'gradient-led', shape: 'soft-rect', decoration: 'sparse', photography: 'bright-airy',
    illustration: 'abstract-shapes', icon: 'thin-line', effects: ['glassmorphism', 'subtle-shadow', 'gradient-overlay'],
    cta: 'pill-accent', hero: 'image-dominant', card: 'glass', border: 'hairline', background: 'mesh-gradient',
    premiumCues: ['subtle-depth', 'considered-color', 'crafted-detail'],
    avoid: ['busy-background', 'low-contrast'],
    componentFamilies: ['hero', 'features', 'pricing', 'cta'],
    libraryPatterns: ['gradient-hero', 'card-stack', 'feature-row'],
    diversityTags: ['depth', 'gradient', 'soft', 'technical'],
    expansion: { palette: 'gradient-led', typeScale: 'balanced', radius: 'round', elevation: 'soft', density: 'comfortable', effects: ['glassmorphism', 'gradient-overlay'], componentBias: { hero: 'image-dominant' } },
  },
  {
    id: 'neo-brutalism', name: 'Neo-Brutalism', family: 'expressive',
    visualPhilosophy: 'honesty', composition: 'asymmetric', layoutPhilosophy: 'type-first',
    grid: 'broken-grid', rhythm: 'staccato', spacing: 'compact', typography: 'grotesque-solo',
    color: 'high-contrast', shape: 'sharp-rect', decoration: 'geometric-accents', photography: 'documentary',
    illustration: 'flat-vector', icon: 'sharp', effects: ['hard-shadow'], cta: 'oversized',
    hero: 'type-dominant', card: 'flat-outline', border: 'bold', background: 'solid',
    premiumCues: ['confident-scale', 'material-honesty'],
    avoid: ['low-contrast', 'tiny-cta'],
    componentFamilies: ['hero', 'cta', 'features', 'stats'],
    libraryPatterns: ['centered-poster', 'feature-row', 'stat-row'],
    diversityTags: ['maximal', 'sharp', 'flat', 'type-led'],
    expansion: { palette: 'high-contrast', typeScale: 'dramatic', radius: 'none', elevation: 'pronounced', density: 'compact', effects: ['hard-shadow'], componentBias: { hero: 'type-dominant', cta: 'oversized' } },
  },
  {
    id: 'modern', name: 'Modern', family: 'minimal',
    visualPhilosophy: 'clarity', composition: 'asymmetric', layoutPhilosophy: 'content-first',
    grid: 'modular', rhythm: 'dynamic', spacing: 'comfortable', typography: 'geometric-sans',
    color: 'muted-neutral-accent', shape: 'soft-rect', decoration: 'sparse', photography: 'bright-airy',
    illustration: 'flat-vector', icon: 'thin-line', effects: ['soft-elevation', 'subtle-shadow'], cta: 'prominent-solid',
    hero: 'split', card: 'elevated', border: 'none', background: 'subtle-gradient',
    premiumCues: ['refined-type', 'considered-color', 'subtle-depth', 'confident-scale'],
    avoid: ['clutter', 'default-fonts'],
    componentFamilies: ['hero', 'features', 'pricing', 'stats', 'cta', 'footer'],
    libraryPatterns: ['split-hero', 'feature-row', 'card-stack'],
    diversityTags: ['minimal', 'soft', 'gradient'],
    expansion: { palette: 'muted-neutral-accent', typeScale: 'balanced', radius: 'medium', elevation: 'soft', density: 'comfortable', effects: ['soft-elevation'], componentBias: { hero: 'split' } },
  },
  {
    id: 'retro', name: 'Retro', family: 'retro',
    visualPhilosophy: 'nostalgia', composition: 'symmetric', layoutPhilosophy: 'type-first',
    grid: 'strict-columns', rhythm: 'steady', spacing: 'comfortable', typography: 'expressive-display',
    color: 'earthy', shape: 'pill', decoration: 'pattern-rich', photography: 'duotone-treated',
    illustration: 'flat-vector', icon: 'solid', effects: ['grain', 'flat'], cta: 'prominent-solid',
    hero: 'type-dominant', card: 'filled', border: 'framed', background: 'pattern',
    premiumCues: ['crafted-detail', 'considered-color', 'tactile'],
    avoid: ['low-contrast'],
    componentFamilies: ['hero', 'features', 'gallery', 'cta'],
    libraryPatterns: ['centered-poster', 'feature-row', 'collage-wall'],
    diversityTags: ['retro', 'vibrant', 'type-led'],
    expansion: { palette: 'earthy', typeScale: 'dramatic', radius: 'pill', elevation: 'flat', density: 'comfortable', effects: ['grain'], componentBias: { hero: 'type-dominant' } },
  },
  {
    id: 'organic', name: 'Organic', family: 'organic',
    visualPhilosophy: 'warmth', composition: 'freeform', layoutPhilosophy: 'image-first',
    grid: 'loose-columns', rhythm: 'flowing', spacing: 'airy', typography: 'humanist-sans',
    color: 'earthy', shape: 'organic-blob', decoration: 'organic-accents', photography: 'lifestyle',
    illustration: 'hand-drawn', icon: 'rounded', effects: ['soft-elevation'], cta: 'pill-accent',
    hero: 'image-dominant', card: 'borderless', border: 'none', background: 'texture',
    premiumCues: ['tactile', 'generous-whitespace', 'considered-color', 'crafted-detail'],
    avoid: ['clutter', 'low-contrast'],
    componentFamilies: ['hero', 'features', 'gallery', 'footer'],
    libraryPatterns: ['image-band', 'card-stack', 'feature-row'],
    diversityTags: ['organic', 'soft', 'image-led'],
    expansion: { palette: 'earthy', typeScale: 'balanced', radius: 'round', elevation: 'soft', density: 'airy', effects: ['soft-elevation'], componentBias: { hero: 'image-dominant' } },
  },
  {
    id: 'luxury-fashion', name: 'Luxury Fashion', family: 'luxe',
    visualPhilosophy: 'opulence', composition: 'editorial-columns', layoutPhilosophy: 'image-first',
    grid: 'baseline-locked', rhythm: 'flowing', spacing: 'generous', typography: 'editorial-serif',
    color: 'monochrome', shape: 'sharp-rect', decoration: 'none', photography: 'editorial',
    illustration: 'none', icon: 'thin-line', effects: ['flat', 'grain'], cta: 'text-link',
    hero: 'full-bleed', card: 'borderless', border: 'none', background: 'solid',
    premiumCues: ['restraint', 'refined-type', 'generous-whitespace', 'crafted-detail', 'confident-scale'],
    avoid: ['clutter', 'clip-art', 'busy-background'],
    componentFamilies: ['hero', 'gallery', 'footer'],
    libraryPatterns: ['magazine-spread', 'image-band', 'minimal-stack'],
    diversityTags: ['luxe', 'editorial', 'mono', 'image-led'],
    expansion: { palette: 'monochrome', typeScale: 'dramatic', radius: 'none', elevation: 'flat', density: 'generous', effects: ['grain'], componentBias: { hero: 'full-bleed' } },
  },
  {
    id: 'premium-medical', name: 'Premium Medical', family: 'corporate',
    visualPhilosophy: 'precision', composition: 'modular-grid', layoutPhilosophy: 'content-first',
    grid: 'strict-columns', rhythm: 'calm', spacing: 'airy', typography: 'humanist-sans',
    color: 'muted-neutral-accent', shape: 'soft-rect', decoration: 'sparse', photography: 'bright-airy',
    illustration: 'flat-vector', icon: 'rounded', effects: ['subtle-shadow'], cta: 'prominent-solid',
    hero: 'split', card: 'elevated', border: 'hairline', background: 'subtle-gradient',
    premiumCues: ['precision', 'generous-whitespace', 'considered-color', 'refined-type'],
    avoid: ['clutter', 'clip-art', 'low-contrast'],
    componentFamilies: ['hero', 'features', 'stats', 'contact', 'footer'],
    libraryPatterns: ['split-hero', 'feature-row', 'stat-row'],
    diversityTags: ['minimal', 'soft', 'technical'],
    expansion: { palette: 'muted-neutral-accent', typeScale: 'balanced', radius: 'medium', elevation: 'soft', density: 'airy', effects: ['subtle-shadow'], componentBias: { hero: 'split' } },
  },
  {
    id: 'scandinavian', name: 'Scandinavian', family: 'minimal',
    visualPhilosophy: 'restraint', composition: 'symmetric', layoutPhilosophy: 'whitespace-led',
    grid: 'strict-columns', rhythm: 'calm', spacing: 'generous', typography: 'grotesque-solo',
    color: 'muted-neutral-accent', shape: 'soft-rect', decoration: 'organic-accents', photography: 'lifestyle',
    illustration: 'line-art', icon: 'thin-line', effects: ['flat', 'subtle-shadow'], cta: 'outline',
    hero: 'minimal', card: 'flat-outline', border: 'hairline', background: 'solid',
    premiumCues: ['restraint', 'material-honesty', 'generous-whitespace', 'refined-type', 'crafted-detail'],
    avoid: ['clutter', 'busy-background', 'rainbow-palette'],
    componentFamilies: ['hero', 'features', 'gallery', 'footer'],
    libraryPatterns: ['minimal-stack', 'feature-row', 'image-band'],
    diversityTags: ['minimal', 'soft', 'organic', 'mono'],
    expansion: { palette: 'muted-neutral-accent', typeScale: 'balanced', radius: 'medium', elevation: 'flat', density: 'generous', effects: ['flat'], componentBias: { hero: 'minimal' } },
  },
  {
    id: 'bento', name: 'Bento', family: 'technical',
    visualPhilosophy: 'clarity', composition: 'bento-grid', layoutPhilosophy: 'grid-led',
    grid: 'bento', rhythm: 'steady', spacing: 'comfortable', typography: 'geometric-sans',
    color: 'muted-neutral-accent', shape: 'soft-rect', decoration: 'sparse', photography: 'studio-clean',
    illustration: 'flat-vector', icon: 'rounded', effects: ['soft-elevation', 'subtle-shadow'], cta: 'pill-accent',
    hero: 'split', card: 'bento-tile', border: 'hairline', background: 'solid',
    premiumCues: ['precision', 'subtle-depth', 'considered-color', 'crafted-detail'],
    avoid: ['clutter', 'low-contrast'],
    componentFamilies: ['hero', 'features', 'stats', 'gallery', 'pricing'],
    libraryPatterns: ['bento-grid', 'card-stack', 'stat-row'],
    diversityTags: ['bento', 'geometric', 'soft', 'technical'],
    expansion: { palette: 'muted-neutral-accent', typeScale: 'balanced', radius: 'round', elevation: 'soft', density: 'comfortable', effects: ['soft-elevation'], componentBias: { hero: 'split' } },
  },
  {
    id: 'gradient-mesh', name: 'Gradient Mesh', family: 'expressive',
    visualPhilosophy: 'futurism', composition: 'layered-overlap', layoutPhilosophy: 'image-first',
    grid: 'modular', rhythm: 'flowing', spacing: 'comfortable', typography: 'geometric-sans',
    color: 'gradient-led', shape: 'organic-blob', decoration: 'organic-accents', photography: 'none',
    illustration: 'abstract-shapes', icon: 'thin-line', effects: ['gradient-overlay', 'glow', 'soft-elevation'],
    cta: 'oversized', hero: 'type-dominant', card: 'glass', border: 'none', background: 'mesh-gradient',
    premiumCues: ['subtle-depth', 'considered-color', 'confident-scale'],
    avoid: ['heavy-gradient-on-text', 'low-contrast', 'busy-background'],
    componentFamilies: ['hero', 'features', 'cta', 'pricing'],
    libraryPatterns: ['gradient-hero', 'card-stack', 'feature-row'],
    diversityTags: ['gradient', 'depth', 'vibrant', 'organic'],
    expansion: { palette: 'gradient-led', typeScale: 'dramatic', radius: 'round', elevation: 'soft', density: 'comfortable', effects: ['gradient-overlay', 'glow'], componentBias: { hero: 'type-dominant' } },
  },
  {
    id: 'memphis', name: 'Memphis', family: 'playful',
    visualPhilosophy: 'experimentation', composition: 'freeform', layoutPhilosophy: 'type-first',
    grid: 'broken-grid', rhythm: 'staccato', spacing: 'compact', typography: 'expressive-display',
    color: 'neon', shape: 'geometric', decoration: 'pattern-rich', photography: 'none',
    illustration: 'abstract-shapes', icon: 'sharp', effects: ['hard-shadow', 'flat'], cta: 'oversized',
    hero: 'type-dominant', card: 'filled', border: 'bold', background: 'pattern',
    premiumCues: ['confident-scale', 'crafted-detail'],
    avoid: ['low-contrast', 'tiny-cta'],
    componentFamilies: ['hero', 'cta', 'features', 'gallery'],
    libraryPatterns: ['collage-wall', 'centered-poster', 'feature-row'],
    diversityTags: ['maximal', 'playful', 'geometric', 'vibrant'],
    expansion: { palette: 'neon', typeScale: 'dramatic', radius: 'none', elevation: 'pronounced', density: 'compact', effects: ['hard-shadow'], componentBias: { hero: 'type-dominant', cta: 'oversized' } },
  },
  {
    id: 'future-ui', name: 'Future UI', family: 'technical',
    visualPhilosophy: 'futurism', composition: 'modular-grid', layoutPhilosophy: 'content-first',
    grid: 'modular', rhythm: 'dynamic', spacing: 'comfortable', typography: 'mono-accent',
    color: 'high-contrast', shape: 'soft-rect', decoration: 'geometric-accents', photography: 'none',
    illustration: '3d', icon: 'duotone', effects: ['glow', 'gradient-overlay', 'soft-elevation'],
    cta: 'pill-accent', hero: 'split', card: 'glass', border: 'hairline', background: 'mesh-gradient',
    premiumCues: ['subtle-depth', 'precision', 'considered-color', 'confident-scale'],
    avoid: ['busy-background', 'low-contrast'],
    componentFamilies: ['hero', 'features', 'stats', 'pricing', 'cta'],
    libraryPatterns: ['bento-grid', 'gradient-hero', 'stat-row'],
    diversityTags: ['technical', 'depth', 'gradient', 'geometric'],
    expansion: { palette: 'high-contrast', typeScale: 'balanced', radius: 'medium', elevation: 'soft', density: 'comfortable', effects: ['glow', 'gradient-overlay'], componentBias: { hero: 'split' } },
  },
];

/** The frozen genome registry. */
export const GENOMES = Object.freeze(RAW.map(deepFreeze));
